import { ApiClient } from '@website/lib/api';
import { browserFetch } from '@website/lib/api/browserFetch';

export interface StripeAccountStatus {
  hasStripeAccount: boolean;
  stripeAccountId?: string;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requiresAction: boolean;
  currentlyDue?: string[];
  lastCreationError?: string;
}

export async function createStripeAccount(params: {
  professionalId: string;
  country?: string;
}): Promise<{ stripeAccountId: string }> {
  return ApiClient.createStripeAccount(params);
}

export async function createAccountSession(stripeAccountId: string): Promise<{ clientSecret: string }> {
  return ApiClient.createAccountSession({ stripeAccountId });
}

export async function getStripeAccountStatus(professionalId: string): Promise<StripeAccountStatus> {
  return ApiClient.getStripeAccountStatus(professionalId);
}

export async function createDashboardLoginLink(professionalId: string): Promise<{ url: string }> {
  return ApiClient.createDashboardLoginLink({ professionalId });
}

export async function createAccountLink(params: {
  professionalId: string;
  returnUrl: string;
  refreshUrl: string;
}): Promise<{ url: string }> {
  return ApiClient.createAccountLink(params);
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  currency: string;
  professionalOnboardingComplete: boolean;
}

export async function createPaymentIntent(bookingId: string): Promise<CreatePaymentIntentResponse> {
  return ApiClient.createPaymentIntent({ bookingId });
}

export interface StartPaymentResponse {
  success: boolean;
}

/**
 * Lock a booking for payment processing (PENDING_PAYMENT → PROCESSING_PAYMENT).
 * This must be called before confirming payment to prevent race conditions with cancellation.
 *
 * @param bookingId - The booking to lock
 * @param paymentIntentId - The PaymentIntent that is about to be confirmed (for atomic locking)
 *
 * Note: Uses browserFetch directly until API client is regenerated.
 */
export async function startPayment(bookingId: string, paymentIntentId: string): Promise<StartPaymentResponse> {
  return browserFetch<StartPaymentResponse>('/stripe/start-payment', {
    method: 'POST',
    body: JSON.stringify({ bookingId, paymentIntentId })
  });
}

// === Payments Dashboard Types ===

export interface PaymentDashboardBooking {
  id: string;
  activityId: string;
  activityTitle: string;
  clientName: string;
  bookingDate: string;
  timeSlot: {
    startTime: string;
    endTime: string;
  };
  totalPrice: number;
  platformFee: number;
  professionalEarnings: number;
  eventEndAt: number;
  settledAt?: number;
  paymentStatus: string;
}

export interface DisputedBooking extends PaymentDashboardBooking {
  disputeStatus: 'pending' | 'accepted' | 'rejected';
  disputeReason?: string;
  disputeOpenedAt: number;
}

export interface PayoutGroup {
  payoutId: string;
  paidOutAt: number;
  totalAmount: number;
  bookingCount: number;
  bookings: PaymentDashboardBooking[];
}

export interface SectionTotal {
  count: number;
  total: number;
}

export interface PaymentsDashboardResponse {
  summary: {
    onHoldTotal: number;
    onHoldCount: number;
    disputedTotal: number;
    disputedCount: number;
    payoutReadyTotal: number;
    payoutReadyCount: number;
    nextPayoutAt: number | null;
  };
  onHold: {
    bookings: PaymentDashboardBooking[];
    settlingNext24h: SectionTotal;
    settlingAfter24h: SectionTotal;
  };
  disputed: {
    bookings: DisputedBooking[];
  };
  payoutReady: {
    bookings: PaymentDashboardBooking[];
  };
  recentPayouts: {
    payouts: PayoutGroup[];
    thisMonthTotal: number;
    lastMonthTotal: number;
  };
  stripeStatus: {
    onboardingComplete: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    requiresAction: boolean;
    currentlyDue?: string[];
  };
}

/**
 * Get payment dashboard data for a professional
 */
export async function getProfessionalPaymentsDashboard(professionalId: string): Promise<PaymentsDashboardResponse> {
  return browserFetch<PaymentsDashboardResponse>(`/stripe/payments-dashboard/${professionalId}`, {
    method: 'GET'
  });
}
